import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import {
  getSyncStatus,
  triggerSync,
  disconnectQuickBooks,
  getQuickBooksAuthUrl,
  getTenantSettings,
  updateTenantSettings,
  type SyncStatusResponse,
  type SyncStatus,
  type TenantSettings,
} from '../services/api';
import type { SyncedEntityType } from '@acitra/shared';
import { DEFAULT_LINE_ITEM_NUMERIC_PRECISION } from '@acitra/shared';
import { DisconnectQBModal } from '../components/DisconnectQBModal';
import { TeamManagementSection } from '../components/TeamManagementSection';
import { EditableValue } from '../components/EditableValue';
import { InfoTooltip } from '../components/InfoTooltip';

// Entity display configuration
const ENTITY_DISPLAY_NAMES: Record<SyncedEntityType, string> = {
  vendors: 'Vendors',
  accounts: 'Accounts',
  bills: 'Bills',
  terms: 'Payment Terms',
  companyInfo: 'Company Info',
  preferences: 'Preferences',
  companyCurrencies: 'Currencies',
};

// Order of entities in the table (exclude companyInfo as it's shown separately)
const ENTITY_ORDER: SyncedEntityType[] = ['vendors', 'accounts', 'bills', 'terms'];

const SYNC_STATUS_LABELS: Record<SyncStatus, string> = {
  idle: 'Not synced',
  in_progress: 'Syncing...',
  success: 'Synced',
  failed: 'Sync failed',
};

export function Settings() {
  const navigate = useNavigate();
  const { currentTenantId, getCurrentTenant, fetchUserTenants, getCurrentUserRole, user } =
    useAuthStore();
  const currentTenant = getCurrentTenant();
  const currentUserRole = getCurrentUserRole();
  const currentUserSub = user?.userId;

  const [syncStatus, setSyncStatus] = useState<SyncStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Settings state
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const fetchSyncStatus = useCallback(async () => {
    if (!currentTenantId) return;

    setIsLoading(true);
    setError(null);

    try {
      const status = await getSyncStatus(currentTenantId);
      setSyncStatus(status);
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sync status');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenantId]);

  const fetchSettings = useCallback(async () => {
    if (!currentTenantId) return;

    setIsLoadingSettings(true);
    setSettingsError(null);

    try {
      const settings = await getTenantSettings(currentTenantId);
      setTenantSettings(settings);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setSettingsError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoadingSettings(false);
    }
  }, [currentTenantId]);

  useEffect(() => {
    fetchSyncStatus();
    fetchSettings();
  }, [fetchSyncStatus, fetchSettings]);

  // Poll for status updates while syncing
  useEffect(() => {
    if (syncStatus?.lastSyncStatus === 'in_progress') {
      const interval = setInterval(() => {
        fetchSyncStatus();
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [syncStatus?.lastSyncStatus, fetchSyncStatus]);

  const handleSync = async () => {
    if (!currentTenantId || isSyncing) return;

    setIsSyncing(true);
    setSyncMessage(null);
    setError(null);

    try {
      const result = await triggerSync(currentTenantId, 'full');
      setSyncMessage(result.message);
      // Refresh status after triggering sync
      await fetchSyncStatus();
    } catch (err) {
      console.error('Failed to trigger sync:', err);
      if (err instanceof Error && err.message.includes('409')) {
        setError('A sync is already in progress');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to trigger sync');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!currentTenantId) return;

    try {
      await disconnectQuickBooks(currentTenantId);
      // Refresh tenants and sync status
      await fetchUserTenants();
      await fetchSyncStatus();
      setShowDisconnectModal(false);
      // If disconnected, redirect to company selection
      navigate('/companies', { replace: true });
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    }
  };

  const handleConnectAnotherCompany = async () => {
    setIsConnecting(true);
    setError(null);
    try {
      const authUrl = await getQuickBooksAuthUrl();
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to get QB auth URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setIsConnecting(false);
    }
  };

  const handlePrecisionChange = async (newPrecision: number) => {
    if (!currentTenantId) return;

    setSettingsError(null);

    const updatedSettings = await updateTenantSettings(currentTenantId, {
      lineItemNumericPrecision: newPrecision,
    });
    setTenantSettings(updatedSettings);
  };

  const canEditSettings = currentUserRole === 'owner' || currentUserRole === 'admin';

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    // Format as YYYY-MM-DD HH:MM
    const date = new Date(dateString);
    const datePart = dateString.split('T')[0];
    const timePart = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${datePart} ${timePart}`;
  };

  if (isLoading && !syncStatus) {
    return (
      <div className="p-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Settings</h1>

      {/* Tenant Info Section */}
      {currentTenant && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Organization</h2>
          </div>
          <div className="px-6 py-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Organization Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{currentTenant.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Subscription</dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">
                  {currentTenant.subscription || 'Free'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {/* Invoice Display Settings Section */}
      {currentTenantId && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Invoice Display Settings</h2>
          </div>
          <div className="px-6 py-4">
            {settingsError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{settingsError}</p>
              </div>
            )}

            {isLoadingSettings ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">
                  Line Item Numeric Precision
                </span>
                <InfoTooltip text="Decimal places for quantity and unit price columns (0-6). Line totals always show 2 decimals." />
                <EditableValue
                  value={
                    tenantSettings?.lineItemNumericPrecision ?? DEFAULT_LINE_ITEM_NUMERIC_PRECISION
                  }
                  onSave={async (val) => {
                    if (val === null) return;
                    const num = parseInt(val, 10);
                    if (!isNaN(num)) {
                      await handlePrecisionChange(num);
                    }
                  }}
                  validate={(val) => {
                    const num = parseInt(val, 10);
                    return !isNaN(num) && num >= 0 && num <= 6;
                  }}
                  disabled={!canEditSettings}
                  inputMode="numeric"
                  textAlign="center"
                />
              </div>
            )}

            {!canEditSettings && !isLoadingSettings && (
              <p className="mt-2 text-xs text-gray-500">
                Only organization owners and admins can modify settings.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Team Management Section - only visible to owners and admins */}
      {currentTenantId &&
        currentUserRole &&
        currentUserSub &&
        ['owner', 'admin'].includes(currentUserRole) && (
          <div className="mb-6">
            <TeamManagementSection
              tenantId={currentTenantId}
              currentUserRole={currentUserRole}
              currentUserSub={currentUserSub}
            />
          </div>
        )}

      {/* QuickBooks Connection Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">QuickBooks Connection</h2>
        </div>
        <div className="px-6 py-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {syncMessage && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-600">{syncMessage}</p>
            </div>
          )}

          {!syncStatus?.quickbooksConnected ? (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">QuickBooks not connected</h3>
              <p className="mt-1 text-sm text-gray-500">
                Connect your QuickBooks account to sync vendors and accounts.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Connection Status */}
              <div>
                <p className="text-sm font-medium text-gray-500">Connection Status</p>
                <div className="flex items-center mt-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500 mr-2"></span>
                  <span className="text-sm text-gray-900">
                    Connected to{' '}
                    <span className="font-medium">
                      {syncStatus.companyInfo?.CompanyName || 'QuickBooks'}
                    </span>
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Since {formatDateTime(syncStatus.quickbooksConnectedAt)}
                </p>
              </div>

              {/* Synced Data Table */}
              {syncStatus.entities && Object.keys(syncStatus.entities).length > 0 && (
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-900">Synced Data</h3>
                    {/* Sync Now Button - only for owners and admins */}
                    {currentUserRole && ['owner', 'admin'].includes(currentUserRole) && (
                      <button
                        onClick={handleSync}
                        disabled={isSyncing || syncStatus.lastSyncStatus === 'in_progress'}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSyncing || syncStatus.lastSyncStatus === 'in_progress' ? (
                          <>
                            <svg
                              className="animate-spin -ml-0.5 mr-1.5 h-4 w-4 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Syncing...
                          </>
                        ) : (
                          <>
                            <svg
                              className="-ml-0.5 mr-1.5 h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                            Sync Now
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Sync Status */}
                  <div className="flex items-center mb-4">
                    <span
                      className={`h-2.5 w-2.5 rounded-full mr-2 ${
                        syncStatus.lastSyncStatus === 'success'
                          ? 'bg-green-500'
                          : syncStatus.lastSyncStatus === 'in_progress'
                            ? 'bg-blue-500'
                            : syncStatus.lastSyncStatus === 'failed'
                              ? 'bg-red-500'
                              : 'bg-gray-400'
                      }`}
                    ></span>
                    <span className="text-sm text-gray-900">
                      {SYNC_STATUS_LABELS[syncStatus.lastSyncStatus]}
                      {syncStatus.lastSyncAt && syncStatus.lastSyncStatus !== 'in_progress' && (
                        <span className="text-gray-500">
                          {' '}
                          - Last synced {formatDateTime(syncStatus.lastSyncAt)}
                        </span>
                      )}
                    </span>
                    {syncStatus.lastSyncStatus === 'in_progress' && (
                      <div className="animate-spin ml-2 h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full"></div>
                    )}
                  </div>

                  {syncStatus.lastSyncError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-600">{syncStatus.lastSyncError}</p>
                    </div>
                  )}
                  <div className="overflow-hidden border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Entity
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Count
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Full Sync
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Update
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {ENTITY_ORDER.map((entityType) => {
                          const info = syncStatus.entities?.[entityType];
                          if (!info) return null;

                          // Get the most recent update (webhook if more recent, otherwise full sync)
                          const lastUpdate = info.lastWebhookUpdateAt
                            ? new Date(info.lastWebhookUpdateAt) >
                              new Date(info.lastFullSyncAt || 0)
                              ? info.lastWebhookUpdateAt
                              : info.lastFullSyncAt
                            : info.lastFullSyncAt;

                          return (
                            <tr key={entityType}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {ENTITY_DISPLAY_NAMES[entityType]}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 text-right">
                                {info.lastFullSyncCount ?? '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 text-right">
                                {info.lastFullSyncAt ? formatDateTime(info.lastFullSyncAt) : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 text-right">
                                {lastUpdate ? formatDateTime(lastUpdate) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Disconnect Button - only for owners */}
              {currentUserRole === 'owner' && (
                <div className="border-t border-gray-200 pt-6">
                  <button
                    onClick={() => setShowDisconnectModal(true)}
                    disabled={syncStatus.lastSyncStatus === 'in_progress'}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      className="-ml-1 mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Disconnect QuickBooks
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Connect Another Company Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Additional QuickBooks Companies</h2>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 mb-4">
            Connect another QuickBooks company to manage multiple businesses from a single account.
          </p>
          <button
            onClick={handleConnectAnotherCompany}
            disabled={isConnecting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isConnecting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                Connecting...
              </>
            ) : (
              <>
                <svg
                  className="-ml-1 mr-2 h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                    fill="#2CA01C"
                  />
                  <path d="M13 7H11V11H7V13H11V17H13V13H17V11H13V7Z" fill="#2CA01C" />
                </svg>
                Connect Another QuickBooks Company
              </>
            )}
          </button>
        </div>
      </div>

      {/* Disconnect Modal */}
      <DisconnectQBModal
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={handleDisconnect}
        companyName={syncStatus?.companyInfo?.CompanyName}
      />
    </div>
  );
}
